import { normalizeCompanyName } from "../contacts/showLeadImport";
import { DOMAINS_NOT_SUPPORTING_FAVICON } from "../misc/unsupportedDomains.const";
import type { Company } from "../types";

// The rules behind "New company" in the company picker. LCCRM-87.
//
// A company created from the contact form used to be a bare name: no website,
// so no logo, and nothing to notice a duplicate by. These derive the website
// from what the form already knows and look for the company before making it.

const GENERIC_MAIL_DOMAINS = new Set(DOMAINS_NOT_SUPPORTING_FAVICON);

/** The host of a website, lowercased and without `www.`; "" when there is none. */
export const websiteDomain = (website: string | null | undefined): string => {
  const trimmed = (website ?? "").trim().toLowerCase();
  if (!trimmed) return "";
  const withoutScheme = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
  const host = withoutScheme.split(/[/?#]/)[0].replace(/:\d+$/, "");
  return host.replace(/^www\./, "");
};

/** A website as it is stored: with a scheme, like CompanyCreate saves it. */
export const normalizeWebsite = (
  website: string | null | undefined,
): string => {
  const trimmed = (website ?? "").trim();
  if (!trimmed) return "";
  return trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
};

/**
 * The website a contact's email suggests for their company, or "" when the
 * address is at a mail provider: jane@gmail.com says nothing about where Jane
 * works, and a wrong website is worse than none.
 */
export const websiteFromEmail = (email: string | null | undefined): string => {
  const domain = (email ?? "").trim().toLowerCase().split("@")[1] ?? "";
  if (!domain || !domain.includes(".")) return "";
  if (GENERIC_MAIL_DOMAINS.has(domain)) return "";
  return `https://${domain}`;
};

/**
 * The company these details already describe, if there is one: the same name
 * once case, punctuation and legal suffixes are set aside, or the same website
 * domain. Name first, since it is what the user typed.
 */
export const findExistingCompany = <
  T extends Pick<Company, "name"> & { website?: string | null },
>(
  companies: T[] | undefined,
  { name, website }: { name: string; website?: string },
): T | undefined => {
  if (!companies) return undefined;
  const wantedName = normalizeCompanyName(name);
  const wantedDomain = websiteDomain(website);
  return (
    (wantedName
      ? companies.find(
          (company) => normalizeCompanyName(company.name) === wantedName,
        )
      : undefined) ??
    (wantedDomain
      ? companies.find(
          (company) => websiteDomain(company.website) === wantedDomain,
        )
      : undefined)
  );
};
